import { ScrollDown } from 'app/assets/svg';
import { useCompletedInfoProfile } from 'hooks/useCompletedInfoProfile';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ScrollLink } from 'react-scroll';
import { Category as CategoryType, GetCategoryDto } from 'shared/api';
import { CONDITION_DESKTOP } from 'shared/common/constants';
import { routes } from 'shared/common/routes';
import { LinkType } from 'shared/common/types';
import { chooseEndingWord } from 'shared/helpers';
import { buildRoute } from 'shared/helpers/buildRoute';
import { Breadcrumbs, BreadcrumbType, CategoryButton } from 'shared/ui';
import styled from 'styled-components';
import { AskExpertForm, ExpertsInCategory, NotCompletedProfileBanner } from 'widgets/common';
import { MainLayout } from 'widgets/layouts';

type CategoryProps = {
  category: GetCategoryDto;
};

const Category: FC<CategoryProps> = ({ category }) => {
  const router = useRouter();
  const { t } = useTranslation('category.page');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbType[]>([]);
  const {
    category: { expertsCount, name },
    rootCategory,
  } = category;
  // eslint-disable-next-line no-unused-vars
  const [_, isShowBanner, setIsShowBanner] = useCompletedInfoProfile({ shownOnlyEmptyInfo: true });

  const totalCountExperts = useMemo(
    () => category.subCategories.reduce((acc, item) => item.expertsCount + acc, expertsCount),
    [router.query],
  );

  const titleOfMainCategory = category.category?.SEODescription
    ? category.category?.SEODescription
    : chooseEndingWord(totalCountExperts, [
        t('head.title.one', { categoryName: name, expertsCount: totalCountExperts }),
        t('head.title.two', { categoryName: name, expertsCount: totalCountExperts }),
        t('head.title.many', { categoryName: name, expertsCount: totalCountExperts }),
      ]);

  const titleOfSubCategory = category.category?.SEODescription
    ? category.category.SEODescription
    : t('head.subCategoryTitle', {
        subCategoryName: name,
        parentCategoryName: rootCategory?.name,
      });

  const head = {
    title: category.rootCategory ? titleOfSubCategory : titleOfMainCategory,
  };
  const navbar: LinkType[] = [
    {
      text: t('navbar.allTopics'),
      href: routes.topics,
    },
    {
      text: t('navbar.becomeExpert'),
      href: routes.expertLanding,
    },
  ];

  useEffect(() => {
    setBreadcrumbs([]);
    if (category.rootCategory?.id) {
      makeBreadCrumbs(category.rootCategory);
    } else {
      setBreadcrumbs([
        { name: t('topContent.rootRubric') },
        { name: router.query.mainCategory ? router.query.mainCategory.toString() : 'Бизнес' },
      ]);
    }
  }, [category]);

  const makeBreadCrumbs = (rootCategory: CategoryType) => {
    setBreadcrumbs((prev) => [
      { name: router.query.mainCategory ? router.query.mainCategory.toString() : 'Бизнес' },
      { name: rootCategory.name, id: rootCategory.id },
      ...prev,
    ]);
    if (rootCategory.rootCategory) {
      makeBreadCrumbs(rootCategory.rootCategory);
    } else {
      setBreadcrumbs((prev) => [{ name: t('topContent.rootRubric') }, ...prev]);
    }
  };

  return (
    <MainLayout head={head} navbar={navbar} hasFooter>
      <Head>
        <meta property="title" content={category.rootCategory ? titleOfSubCategory : titleOfMainCategory} />
      </Head>
      <TopContent>
        <div className="container">
          <Breadcrumbs
            breadcrumbs={
              breadcrumbs.length === 5 ? [breadcrumbs[0], breadcrumbs[1], breadcrumbs[2], breadcrumbs[4]] : breadcrumbs
            }
            routeTemplate={routes.category}
            rootRoute={routes.topics}
          />
          <Title>{t('title', { categoryName: category.category.name })}</Title>

          {!!category.subCategories.length && (
            <Subcategory>
              <SubcategoryText>{t('topContent.subRubrics')}</SubcategoryText>
              <SubcategoryList>
                {category.subCategories.map((item) => (
                  <CategoryButton
                    key={item.id}
                    label={item.name}
                    href={buildRoute(routes.category, { id: item.id })}
                    quantity={item.expertsCount}
                  />
                ))}
              </SubcategoryList>
            </Subcategory>
          )}

          <AskExpertForm
            expertsCount={totalCountExperts}
            threeBestExpert={category.threeBestExpert}
            categoryId={category.category.id}
          />

          <FindExpertYourself>{t('topContent.findYourself')}</FindExpertYourself>
        </div>

        <ScrollButton>
          <ScrollLink to="experts" smooth duration={500}>
            <ScrollDown />
          </ScrollLink>
        </ScrollButton>
      </TopContent>

      {isShowBanner && (
        <NotCompletedProfileBanner
          title={t('banners.notCompleted.title')}
          description={t('banners.notCompleted.description')}
          buttonText={t('banners.notCompleted.buttonText')}
          action={() => router.replace(`${routes.profile}?activeTab=expertProfile`)}
          onClose={() => setIsShowBanner(false)}
        />
      )}
      <ExpertsInCategory
        sortCriteria="rating"
        title={t('experts.mostPopular', { categoryName: category.category.name })}
        limit={6}
        categoryName={category.category.name}
        categoryId={category.category.id}
        subCategories={category.category.subCategories}
      />
      {/* CR-1061 start */}
      {/* <ExpertsInCategory
        sortCriteria="rating"
        title={t('experts.bestReviews', { categoryName: category.category.name })}
        limit={6}
        categoryName={category.category.name}
        categoryId={category.category.id}
        subCategories={category.category.subCategories}
      /> */}
      {/* CR-1061 end */}
    </MainLayout>
  );
};

export default Category;

const TopContent = styled.div`
  position: relative;
  background-image: var(--gradient2);
  color: var(--white);
  padding-top: 30px;
  padding-bottom: 34px;
  ${CONDITION_DESKTOP} {
    min-width: 1180px;
  }
`;

const Title = styled.h1`
  font-weight: bold;
  font-size: 21px;
  margin: 30px 0 20px;
`;
const Subcategory = styled.div``;
const SubcategoryText = styled.p`
  font-size: 15px;
  opacity: 0.7;
  margin-bottom: 15px;
`;
const SubcategoryList = styled.div``;
const FindExpertYourself = styled.p`
  text-align: center;
  font-weight: bold;
  font-size: 15px;
`;
const ScrollButton = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translate(-50%, -50%);
  width: 42px;
  height: 42px;
  border: 1px solid var(--white);
  border-radius: 50%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  overflow: hidden;
  &:hover {
    svg {
      transform: scale(1.1);
    }
  }
`;
